import { useEffect, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { useClickOutside } from '@mantine/hooks';
import { isBoolean } from 'lodash';

import classes from './Filter.module.css';
import Chevron from 'static/icons/chevron.svg';
import { Option } from 'utils/interfaces/commonInterfacesAndTypes';
import Checkbox from '../Checkbox/Checkbox';
import { FilledButton, OutlinedButton } from 'components/common/Button/Button';
import RadioButton from '../RadioButton/RadioButton';
import useTranslation from 'next-translate/useTranslation';
import {AnnouncementFiltersKey} from "../../../Layout/NavBar/Filters/Filters";

type FilterProps = {
   filterType: 'checkbox' | 'radioButton';
   selectedOptions: string | string[];
   options: Option[];
   placeholder: string;
   filterName: AnnouncementFiltersKey;
   onChangeHandler: (filterName: AnnouncementFiltersKey, value: string) => void;
   onSubmitHandler?: () => void;
   onClearHandler?: (filterName: AnnouncementFiltersKey) => void;
   expandedInPlace?: boolean;
   closeOnClickOutside?: boolean;
   onClickToggle?: (filterName: AnnouncementFiltersKey, openValue: boolean) => void;
   isFilterOpen?: boolean;
};

const Filter = ({
   filterType,
   selectedOptions,
   onChangeHandler,
   options,
   placeholder,
   filterName,
   onClearHandler,
   onSubmitHandler,
   expandedInPlace,
   closeOnClickOutside = true,
   onClickToggle,
   isFilterOpen,
}: FilterProps) => {
   const [isOpen, setIsOpen] = useState(false);
   const ref = useClickOutside(() => setIsOpen(false));
   const { t } = useTranslation('common');

   useEffect(() => {
      if (isBoolean(isFilterOpen)) {
         setIsOpen(isFilterOpen);
      }
   }, [isFilterOpen]);

   const clearValuesHandler = () => {
      setIsOpen(false);
      onClearHandler && onClearHandler(filterName as AnnouncementFiltersKey);
   };

   const submitFilterHandler = () => {
      setIsOpen(false);
      onSubmitHandler && onSubmitHandler();
   };

   const clickToggleHandler = (isOpenValue: boolean) => {
      onClickToggle ? onClickToggle(filterName, isOpenValue) : setIsOpen(isOpenValue);
   };

   let optionsContent;

   if (filterType === 'checkbox') {
      optionsContent = options.map(option => (
         <div
            onClick={() => onChangeHandler(filterName as AnnouncementFiltersKey, option.value)}
            className={classNames(classes.option, { [classes.expandedInPlace]: expandedInPlace })}
            key={option.value}
         >
            <Checkbox
               id={option.value}
               label={option.label}
               name={filterName}
               value={selectedOptions.includes(option.value)}
               readonly
            />
            {option.description && (
               <p className={classes.description}>{t('' + option.description)}</p>
            )}
         </div>
      ));
   } else {
      optionsContent = options.map(option => (
         <div
            onClick={() => onChangeHandler(filterName as AnnouncementFiltersKey, option.value)}
            className={classNames(classes.option, { [classes.expandedInPlace]: expandedInPlace })}
            key={option.value}
         >
            <RadioButton
               id={option.value}
               label={t('' + option.label)}
               name={filterName}
               value={option.value}
               checked={selectedOptions === option.value}
               readonly
            />
         </div>
      ));
   }

   return (
      <div
         ref={closeOnClickOutside ? ref : undefined}
         className={classNames(classes.container, {
            [classes.expandedInPlace]: expandedInPlace,
            [classes.active]: isOpen,
         })}
      >
         <div
            {...(!isOpen && {
               onClick: () => clickToggleHandler(true),
            })}
            className={classNames(classes.select, {
               [classes.expandedInPlace]: expandedInPlace,
            })}
         >
            {placeholder}
            {filterType === 'checkbox' && selectedOptions.length > 0 && (
               <div className={classes.counter}>{selectedOptions.length}</div>
            )}
            {filterType === 'radioButton' && selectedOptions && (
               <div className={classes.counter}>1</div>
            )}

            <div
               onClick={() => clickToggleHandler(!isOpen)}
               className={classNames(classes.iconContainer, {
                  [classes.iconContainerActive]: isOpen,
               })}
            >
               <Image src={Chevron} alt="Chevron" />
            </div>
         </div>
         <div
            className={classNames(
               classes.dropdown,
               { [classes.active]: isOpen },
               {
                  [classes.expandedInPlace]: expandedInPlace,
               },
            )}
         >
            {optionsContent}
            {onSubmitHandler && clearValuesHandler && (
               <div className={classes.buttonsSection}>
                  <FilledButton
                     type="button"
                     onClick={submitFilterHandler}
                     content={t('apply_filters')}
                     className={classes.btn}
                  />
                  <OutlinedButton
                     type="button"
                     onClick={clearValuesHandler}
                     content={t('clear')}
                     className={classes.btn}
                  />
               </div>
            )}
         </div>
      </div>
   );
};

export default Filter;
